import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { formatCurrency, formatDateTime } from "@/lib/format";

type Product = components["schemas"]["ProductResponse"];

export function ProductPage() {
  const { productId } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get("organisationId") ?? undefined;

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!productId || !organisationId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function fetchProduct() {
      setLoading(true);
      const { data, error: apiError } = await api.GET("/v1/products/{id}", {
        params: { path: { id: productId! } },
        headers: { "x-dolfin-organisation-id": organisationId! },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load product.");
      } else {
        setProduct(data ?? null);
      }
      setLoading(false);
    }

    fetchProduct();
    return () => {
      cancelled = true;
    };
  }, [productId, organisationId]);

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Product</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      ) : product ? (
        <Card>
          <CardHeader>
            <CardTitle>
              {product.name || <span className="text-muted-foreground">Unnamed</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Section title="Product">
              <Field label="Product ID">
                <Badge variant="secondary" className="font-mono text-xs">
                  {product.id}
                </Badge>
              </Field>
              <Field label="Organisation">
                {product.organisationId ? (
                  <Link to={`/organisations/${product.organisationId}`}>
                    <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                      {product.organisationId}
                    </Badge>
                  </Link>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </Field>
              <Field label="Type">
                <Badge variant={product.type === "Service" ? "secondary" : "outline"}>
                  {product.type}
                </Badge>
              </Field>
              <Field label="Unit price">
                {formatCurrency(product.unitPrice, product.currency)}
              </Field>
              <Field label="Currency">{product.currency}</Field>
              <Field label="Description" className="sm:col-span-3">
                {product.description ?? "—"}
              </Field>
            </Section>

            <Section title="Audit">
              <Field label="Created">{formatDateTime(product.createdAt)}</Field>
              <Field label="Created by">
                <ActorLink actorId={product.createdBy} />
              </Field>
              <Field label="Updated">{formatDateTime(product.updatedAt)}</Field>
              <Field label="Updated by">
                <ActorLink actorId={product.updatedBy} />
              </Field>
            </Section>
          </CardContent>
        </Card>
      ) : !organisationId ? (
        <p className="text-muted-foreground">
          Missing organisationId query parameter — product endpoint requires an organisation context.
        </p>
      ) : (
        <p className="text-muted-foreground">Product not found.</p>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">{children}</dl>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function ActorLink({ actorId }: { actorId: string | null | undefined }) {
  if (!actorId) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to={`/users/${actorId}`}>
      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
        {actorId.slice(0, 8)}...
      </Badge>
    </Link>
  );
}
